import { useRef, useState } from 'react';
import { FaCog } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useDismissOnOutside } from '../../../Hooks/useDismissOnOutside';
import { useSignOut } from '../../../Hooks/useSignOut';
import { useTempEdgeTranslator } from '../../../Hooks/useTranslation';
import { getInitials } from '../../../utils/helpers';
import { toTestId } from '../../../utils/helpers/toTestId';
import { useSettingsItems } from './NavMenu';
import SignOutButton from './SignOutButton';
import './AccountMenu.css';

const EMPTY_USER = {};

const AccountMenu = () => {
  const { tempedgeTranslator, locale: lang } = useTempEdgeTranslator();
  const containerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const settingsItems = useSettingsItems();
  const { firstName, lastName, email } = useSelector((state) => state.tempEdge.login.portalUserList?.[0]?.user || EMPTY_USER);
  const hasProfile = Boolean(firstName || lastName || email);

  useDismissOnOutside(containerRef, open, () => setOpen(false));

  const handleSignOut = useSignOut(() => setOpen(false));

  return (
    <div className="te-account-menu" ref={containerRef}>
      <button
        type="button"
        className="te-icon-btn"
        aria-expanded={open}
        aria-label={tempedgeTranslator('com.tempedge.msg.menu.settings')}
        onClick={() => setOpen((prev) => !prev)}
        data-testid="account-menu-button"
      >
        <FaCog size={16} />
      </button>

      {open && (
        <div className="te-account-panel" data-testid="account-menu-panel">
          {hasProfile && (
            <>
              <div className="te-account-user" data-testid="account-menu-user">
                <span className="te-account-avatar" data-testid="account-menu-user-avatar">
                  {getInitials(firstName, lastName)}
                </span>
                <span className="te-account-user-info">
                  <span className="te-account-user-name" data-testid="account-menu-user-name">
                    {firstName} {lastName}
                  </span>
                  <span className="te-account-user-email" title={email} data-testid="account-menu-user-email">
                    {email}
                  </span>
                </span>
              </div>

              <div className="te-account-divider" />
            </>
          )}

          {settingsItems.map((item) => (
            <Link
              key={item.optionPath}
              className="te-focus-ring-inset"
              to={`${item.optionPath}/${lang}`}
              onClick={() => setOpen(false)}
              data-testid={`account-menu-item-${toTestId(item.optionPath)}`}
            >
              {tempedgeTranslator(item.optionName)}
            </Link>
          ))}

          {settingsItems.length > 0 && <div className="te-account-divider" />}

          <SignOutButton className="te-account-signout te-focus-ring-inset" onClick={handleSignOut} testId="account-menu-signout" />
        </div>
      )}
    </div>
  );
};

export default AccountMenu;
